import { memo, useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";

import { refreshCurrentElement, setSimpleValue } from "../../../../../../store/actions";
import { currentMediaItemSelector, getSimpleValue } from "../../../../../../store/selectors";
import AudioRecordingControls from "../AudioRecordingControls/AudioRecordingControls";
import AudioRecordingCountdown from "../AudioRecordingCountdown/AudioRecordingCountdown";

// TODO remove when `mic-recorder-to-mp3` gets a new version.
// Vite (Rollup) treats all code as ES6 modules, hence `strict mode` rules apply (accidentally created global var definitions).
// even though lamejs released a new version 1.2.1, the `mic-recorder-to-mp3` version 2.2.2
// bundles an older version of lamejs where the issue has not been fixed.
// issue: https://github.com/zhuker/lamejs/issues/58
globalThis.BitStream = null;
globalThis.GainAnalysis = null;
globalThis.Lame = null;
globalThis.MPEGMode = null;
globalThis.Presets = null;
globalThis.Quantize = null;
globalThis.QuantizePVT = null;
globalThis.Reservoir = null;
globalThis.Takehiro = null;

const STATES = {
  idle: "idle",
  preparing: "preparing",
  recording: "recording",
};

// interface AudioRecordingProps {
//   deviceId: proptypes.string,
//   elementId: proptypes.number,
//   open: proptypes.bool,
// };

function AudioRecording(props) {
  const { open, deviceId, elementId } = props;

  const dispatch = useDispatch();

  const [state, setState] = useState(STATES.idle);
  const [loadedModule, setLoadedModule] = useState(false);

  const { mediaItem } = useSelector(currentMediaItemSelector);

  const stream = useRef(null);
  const recorder = useRef(null);
  const closed = useRef(false);

  const startPreparing = async () => {
    stream.current = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: { deviceId: { exact: deviceId } },
    });

    setState(STATES.preparing);

    if (closed.current) {
      stopRecording();
    }
  };

  const onRecorderFinished = ([, blob]) => {
    const file = new File([blob], `audio-${crypto.randomUUID()}.mp3`, { type: blob.type, lastModified: Date.now() });
    dispatch(refreshCurrentElement({ selectedFile: file }));
    dispatch(
      setSimpleValue("voice-memo-recording", {
        elementId,
        open: false,
        file,
      }),
    );
  };

  const startRecording = () => {
    recorder.current.start();
    setState(STATES.recording);
  };

  const stopRecording = () => {
    stream.current && stream.current.getTracks().forEach((x) => x.stop());
    stream.current = null;
    recorder.current && recorder.current.stop();
  };

  const loadRecordingModule = () => {
    if (recorder.current) {
      recorder.current.config.deviceId = deviceId;
      return;
    }

    import("mic-recorder-to-mp3").then((module) => {
      recorder.current = new module.default({ startRecordingAt: 0, deviceId });
      setLoadedModule(true);
    });
  };

  const onStopClick = () => {
    if (state === STATES.recording) {
      recorder.current && recorder.current.stop().getMp3().then(onRecorderFinished);
    } else {
      dispatch(setSimpleValue("voice-memo-recording", {}));
    }
    setState(STATES.idle);
  };

  const onRestartClick = async () => {
    if (window.confirm("Do you really want to restart the recording?")) {
      stopRecording();
      await startPreparing();
    }
  };

  const onDeleteClick = () => {
    if (window.confirm("Do you really want to cancel the recording?")) {
      dispatch(setSimpleValue("voice-memo-recording", {}));
    }
  };

  useEffect(() => {
    if (open) {
      closed.current = false;
      loadRecordingModule();
      startPreparing();
      return () => {
        closed.current = true;
        stopRecording();
      };
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      dispatch(setSimpleValue("voice-memo-recording", {}));
    }
  }, [mediaItem?.id]);

  if (!open) {
    return;
  }

  return (
    <div>
      <AudioRecordingControls
        state={state}
        stream={stream.current}
        onStop={onStopClick}
        onRestart={onRestartClick}
        onDelete={onDeleteClick}
      />
      <AudioRecordingCountdown state={state} readyToBegin={loadedModule} onStart={startRecording} />
    </div>
  );
}

const mapStateToProps = (state) => {
  const { open, elementId, deviceId } = getSimpleValue(state, "voice-memo-recording", {});
  return {
    open,
    elementId,
    deviceId,
  };
};

export default compose(connect(mapStateToProps), memo)(AudioRecording); // TODO
